import React, {useEffect, useState} from "react"

interface IFrameProps extends React.IframeHTMLAttributes<HTMLIFrameElement> {
    content?: string
}

const IFrame = React.forwardRef<HTMLIFrameElement, IFrameProps>(
    (
        {
            content,
            src,
            width = 560,
            height = 315,
            title = "",
            allowFullScreen = true,
            allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
            referrerPolicy = "strict-origin-when-cross-origin",
            ...props
        },
        ref
    ) => {
        const [iframeAttributes, setIframeAttributes] = useState<
            React.IframeHTMLAttributes<HTMLIFrameElement>
        >({})

        useEffect(() => {
            if (content) {
                const parser = new DOMParser()
                const doc = parser.parseFromString(content, "text/html")
                const iframe = doc.querySelector("iframe")
                if (iframe) {
                    const attrs = Array.from(iframe.attributes).reduce(
                        (acc, attr) => {
                            if (attr.name === "src") {
                                acc[attr.name] = sanitizeUrl(attr.value)
                            } else if (
                                [
                                    "width",
                                    "height",
                                    "title",
                                    "allow",
                                    "referrerpolicy",
                                ].includes(attr.name)
                            ) {
                                acc[attr.name] = attr.value
                            } else if (
                                attr.name.toLowerCase() === "allowfullscreen"
                            ) {
                                acc["allowFullScreen"] = attr.value
                            }
                            return acc
                        },
                        {} as { [key: string]: string | boolean }
                    )
                    setIframeAttributes(attrs)
                } else {
                    console.error("Invalid iframe content")
                }
            } else {
                setIframeAttributes({
                    ...props,
                    src: sanitizeUrl(src || ""),
                    width,
                    height,
                    title,
                    allowFullScreen,
                    allow,
                    referrerPolicy,
                })
            }
        }, [
            content,
            src,
            width,
            height,
            title,
            allowFullScreen,
            allow,
            referrerPolicy,
        ])

        const sanitizeUrl = (url: string): string => {
            try {
                const parsed = new URL(url)
                if (["http:", "https:"].includes(parsed.protocol)) {
                    return url
                }
            } catch {
                /* empty */
            }
            return "about:blank" // Return safe value if URL is invalid or dangerous
        }

        // Remove srcDoc attribute, because it's contained dangerous content
        delete iframeAttributes.srcDoc

        if (!iframeAttributes.src || iframeAttributes.src === "about:blank") {
            return (
                <p className={"text text-muted-foreground"}>
                    Недійсне джерело iframe
                </p>
            )
        }

        return <iframe ref={ref} {...iframeAttributes} />
    }
)
IFrame.displayName = "IFrame"

export { IFrame }
